// C:\Users\server\Desktop\FRONTEND\app-user-front\src\pages\Myaccount.js
import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Spinner,
  Alert,
  Toast,
  Modal,
} from "react-bootstrap";
import CONFIG from "../config";

/* ============================
   Helpers: detect login info
   ============================ */
function getLoggedInEmail() {
  try {
    if (window && window.__USER__?.email)
      return String(window.__USER__.email).toLowerCase();
    if (window && window.__auth?.user?.email)
      return String(window.__auth.user.email).toLowerCase();

    const tryKeys = [
      "user",
      "auth_user",
      "profile",
      "auth",
      "currentUser",
      "current_user",
    ];
    for (const key of tryKeys) {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.email) return String(parsed.email).toLowerCase();
        if (parsed?.user?.email) return String(parsed.user.email).toLowerCase();
      } catch {
        if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(raw)) return raw.toLowerCase();
      }
    }

    const direct =
      localStorage.getItem("userEmail") ||
      localStorage.getItem("email") ||
      localStorage.getItem("user");
    if (direct && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(direct))
      return direct.toLowerCase();
  } catch {}
  return "";
}

function getAuthToken() {
  try {
    // common keys
    const keys = [
      "auth_token",
      "token",
      "access_token",
      "jwt",
      "Authorization",
      "authToken",
    ];
    for (const k of keys) {
      const v = localStorage.getItem(k);
      if (v && v.trim()) return v.trim();
    }

    // maybe stored inside 'user' key as JSON
    const raw = localStorage.getItem("user");
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed?.token) return parsed.token;
        if (parsed?.access_token) return parsed.access_token;
        if (parsed?.auth?.token) return parsed.auth.token;
      } catch {
        // raw might be the token string itself
        if (/^[A-Za-z0-9\-\._~\+\/]+=*$/.test(raw)) return raw;
      }
    }

    // window globals
    if (window && window.__TOKEN__) return window.__TOKEN__;
    return "";
  } catch {
    return "";
  }
}

/* ============================
   Helper: format date-time nicely
   ============================ */
function formatDateTime(dateString) {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString; // fallback: show raw if invalid
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/* ============================
   Main Component
   ============================ */
export default function Myaccount() {
  const loginEmail = getLoggedInEmail(); // detected email (lowercased)
  const token = getAuthToken(); // optional auth token

  const [user, setUser] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState({ show: false, msg: "" });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Helper to make request with optional auth header
  const doGet = (url) => {
    const cfg = {};
    if (token)
      cfg.headers = {
        Authorization: token.startsWith("Bearer") ? token : `Bearer ${token}`,
      };
    console.debug("MyAccountDebug: GET", url, cfg);
    return axios.get(url, cfg);
  };

  const doPut = (url, data) => {
    const cfg = {};
    if (token)
      cfg.headers = {
        Authorization: token.startsWith("Bearer") ? token : `Bearer ${token}`,
      };
    console.debug("MyAccountDebug: PUT", url, cfg, data);
    return axios.put(url, data, cfg);
  };

  const doDelete = (url) => {
    const cfg = {};
    if (token)
      cfg.headers = {
        Authorization: token.startsWith("Bearer") ? token : `Bearer ${token}`,
      };
    console.debug("MyAccountDebug: DELETE", url, cfg);
    return axios.delete(url, cfg);
  };

  // Load user — extremely defensive:
  useEffect(() => {
    if (!loginEmail && !token) {
      console.warn(
        "MyAccountDebug: no loginEmail and no token — redirecting to /login"
      );
      window.location.href = "/login";
      return;
    }

    const load = async () => {
      setLoading(true);
      setError("");
      try {
        const apiBase = (CONFIG?.API_URL || "").replace(/\/$/, "");
        if (!apiBase) throw new Error("CONFIG.API_URL not set");

        const candidates = [];

        // Prefer secure /me endpoints (token-based)
        if (token) {
          candidates.push({
            url: `${apiBase}/me`,
            method: "get",
            useToken: true,
          });
          candidates.push({
            url: `${apiBase}/user/me`,
            method: "get",
            useToken: true,
          });
          candidates.push({
            url: `${apiBase}/auth/me`,
            method: "get",
            useToken: true,
          });
        }

        // Fallback to email-based endpoints (pass email param)
        if (loginEmail) {
          candidates.push({
            url: `${apiBase}/users?email=${encodeURIComponent(loginEmail)}`,
            method: "get",
          });
          candidates.push({
            url: `${apiBase}/user?email=${encodeURIComponent(loginEmail)}`,
            method: "get",
          });
          candidates.push({
            url: `${apiBase}/user/me?email=${encodeURIComponent(loginEmail)}`,
            method: "get",
          });
        }

        // If nothing yet, try generic /users and filter client-side (safe guard)
        candidates.push({ url: `${apiBase}/users`, method: "get" });

        let response = null;
        let found = null;

        for (const c of candidates) {
          try {
            if (c.method === "get") {
              response = await doGet(c.url);
            } else {
              // currently only GET used here
              response = await doGet(c.url);
            }
          } catch (err) {
            console.warn(
              "MyAccountDebug: candidate failed",
              c.url,
              err?.response?.status || err.message
            );
            response = null;
          }

          if (!response || !response.data) continue;

          // Normalize various shapes:
          let payload = response.data;
          // if response has `user` or `data` or `users` etc.
          if (payload.user) payload = payload.user;
          else if (
            payload.data &&
            (Array.isArray(payload.data) ? payload.data.length : true)
          )
            payload = payload.data;
          // If array -> try to pick the record that matches loginEmail
          if (Array.isArray(payload)) {
            if (loginEmail) {
              const match = payload.find((p) => {
                if (!p) return false;
                const em = (
                  p.email ||
                  p.user_email ||
                  p.owner_email ||
                  ""
                )
                  .toString()
                  .toLowerCase();
                return em === loginEmail;
              });
              if (match) {
                found = match;
                console.debug(
                  "MyAccountDebug: matched user from array response",
                  c.url
                );
                break;
              } else {
                // maybe backend returned only one element array
                if (payload.length === 1) {
                  found = payload[0];
                  console.debug(
                    "MyAccountDebug: took single-array element",
                    c.url
                  );
                  break;
                } else {
                  // no match, continue trying
                  continue;
                }
              }
            } else {
              // no loginEmail but token used — if single element, accept it
              if (payload.length === 1) {
                found = payload[0];
                break;
              } else {
                continue;
              }
            }
          } else if (typeof payload === "object") {
            // object — ensure email match if possible
            const em = (
              payload.email ||
              payload.user_email ||
              payload.owner_email ||
              ""
            )
              .toString()
              .toLowerCase();
            if (!loginEmail || (em && em === loginEmail) || c.url.includes("/me")) {
              found = payload;
              console.debug("MyAccountDebug: found object payload", c.url);
              break;
            } else {
              // email mismatch — do not accept
              console.warn(
                "MyAccountDebug: object response email mismatch (won't accept) from",
                c.url,
                "email:",
                em
              );
              continue;
            }
          }
        } // end for candidates

        if (!found)
          throw new Error(
            "Could not find logged-in user's profile from any endpoint (check backend)"
          );

        // final safety: ensure found's email equals loginEmail if loginEmail exists
        if (loginEmail) {
          const foundEmail = (
            found.email ||
            found.user_email ||
            found.owner_email ||
            ""
          )
            .toString()
            .toLowerCase();
          if (foundEmail !== loginEmail) {
            console.error(
              "MyAccountDebug: final email mismatch — refusing to show wrong user",
              { loginEmail, foundEmail }
            );
            throw new Error(
              "Security check failed: server returned different user's data"
            );
          }
        }

        setUser(found);
        setForm({
          name: found.name || "",
          email: found.email || "",
          phone: found.phone || "",
          address: found.address || "",
        });
      } catch (err) {
        console.error(
          "MyAccountDebug: load user error:",
          err?.response?.data || err.message || err
        );
        setError("Failed to load your profile.");
      } finally {
        setLoading(false);
      }
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [formState, setFormState] = useState(form);
  useEffect(() => setFormState(form), [form]);

  const handleChange = (k, v) => setFormState((p) => ({ ...p, [k]: v }));

  // Save profile — use auth header if token present, and strict check
  const saveProfile = async () => {
    if (!user) return;
    setSaving(true);
    setError("");
    try {
      // strict: don't allow changing any other user's id
      const url = `${(CONFIG?.API_URL || "").replace(/\/$/, "")}/users/${user.id}`;
      await doPut(url, formState);

      // if email changed — force logout
      const newEmail = (formState.email || "").toString().toLowerCase();
      if (newEmail && newEmail !== (loginEmail || "").toString().toLowerCase()) {
        setToast({ show: true, msg: "Email changed — logging out for security..." });
        setTimeout(() => {
          localStorage.clear();
          window.location.href = "/login";
        }, 1200);
        return;
      }

      setUser((p) => ({ ...p, ...formState }));
      setToast({ show: true, msg: "Profile updated" });
    } catch (err) {
      console.error(
        "MyAccountDebug: saveProfile failed",
        err?.response?.data || err.message
      );
      setError("Failed to update profile.");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!user) return;
    setDeleting(true);
    setError("");
    try {
      const url = `${(CONFIG?.API_URL || "").replace(/\/$/, "")}/users/${user.id}`;
      await doDelete(url);
      setToast({ show: true, msg: "Account deleted. Redirecting..." });
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/login";
      }, 1000);
    } catch (err) {
      console.error(
        "MyAccountDebug: delete failed",
        err?.response?.data || err.message
      );
      setError("Failed to delete account.");
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />{" "}
        <div className="mt-2">Loading profile...</div>
      </Container>
    );
  }

  if (error) {
    return (
      <div style={page}>
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&family=League+Spartan:wght@700&display=swap');
        `}</style>

        {/* HERO */}
        <section style={heroSection}>
          <h1 style={heroTitle}>My Account</h1>
          <p style={heroSubtitle}>
            Manage your profile details, update your information, and keep your
            Swapiify account secure.
          </p>
        </section>

        <Container className="py-5">
          <Alert variant="danger">{error}</Alert>
          <div style={{ fontSize: 13, marginTop: 12, color: "#666" }}>
            <strong>Debug:</strong> check console for `MyAccountDebug:` logs to
            see which endpoints were tried.
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={page}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&family=League+Spartan:wght@700&display=swap');
      `}</style>

      {/* ================= FULL-WIDTH HERO (same style as Aboutus) ================= */}
      <section style={heroSection}>
        <h1 style={heroTitle}>My Account</h1>
        <p style={heroSubtitle}>
          View and update your personal details, email, and phone to keep your
          Swapiify profile up to date.
        </p>
      </section>

      {/* ================= ORIGINAL ACCOUNT LAYOUT ================= */}
      <Container className="py-4">
        <Row>
          <Col md={6}>
            <Card>
              <Card.Header>My Account</Card.Header>
              <Card.Body>
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      value={formState.name}
                      onChange={(e) =>
                        handleChange("name", e.target.value)
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Email </Form.Label>
                    <Form.Control
                      value={formState.email}
                      onChange={(e) =>
                        handleChange("email", e.target.value)
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      value={formState.phone}
                      onChange={(e) =>
                        handleChange("phone", e.target.value)
                      }
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    {/* address / extra field reserved - kept as in your original code */}
                  </Form.Group>
                </Form>
              </Card.Body>

              <Card.Footer className="d-flex justify-content-between">
                <Button onClick={saveProfile} disabled={saving}>
                  {saving ? "Saving..." : "Save Changes"}
                </Button>

                <Button
                  variant="danger"
                  onClick={() => setShowDeleteModal(true)}
                >
                  Delete Account
                </Button>
              </Card.Footer>
            </Card>
          </Col>

          <Col md={6}>
            <Card>
              <Card.Header>Account Info</Card.Header>
              <Card.Body>
                <p>
                  <strong>ID:</strong> {user?.id}
                </p>
                <p>
                  <strong>Email:</strong> {user?.email}
                </p>
                <p>
                  <strong>Created:</strong> {formatDateTime(user?.created_at)}
                </p>
                <p>
                  <strong>Updated:</strong> {formatDateTime(user?.updated_at)}
                </p>
                <div
                  style={{ marginTop: 8, fontSize: 12, color: "#666" }}
                ></div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Toast */}
        <div
          style={{ position: "fixed", right: 20, bottom: 20 }}
        >
          <Toast
            show={toast.show}
            onClose={() => setToast({ show: false, msg: "" })}
            autohide
            delay={2500}
          >
            <Toast.Header>
              <strong>Account</strong>
            </Toast.Header>
            <Toast.Body>{toast.msg}</Toast.Body>
          </Toast>
        </div>

        {/* Delete Modal */}
        <Modal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>Delete Account?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-danger fw-bold">
              This action cannot be undone.
            </p>
            <p>Are you sure you want to permanently delete your account?</p>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => setShowDeleteModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </div>
  );
}

/* ---------------- HERO STYLES (copied style from Aboutus & adapted text) ---------------- */

const page = {
  fontFamily: "Roboto, sans-serif",
  background: "#fff",
  paddingBottom: "60px",
};

const heroSection = {
  width: "100%",
  background: "#6a2d57",
  padding: "80px 20px",
  textAlign: "center",
  borderRadius: 0,

  // top right bottom left
  margin: "0 0 50px 0",
};

const heroTitle = {
  fontSize: "48px",
  fontFamily: "League Spartan, sans-serif",
  color: "#ff8b5a", // coral
  marginBottom: "10px",
};

const heroSubtitle = {
  fontSize: "16px",
  color: "#ffffffff",
  maxWidth: "700px",
  margin: "0 auto",
  lineHeight: 1.6,
};
